/**
 * Gateway — the first page every visitor sees at '/'.
 *
 * This is a decision screen only. It is NOT the landing page.
 *
 *   Customer  →  /remodify   (customer / portal flow)
 *   Provider  →  /landing    (provider onboarding / marketing)
 *
 * Rules enforced by product:
 *   • All wording is static (no typewriter, no delayed text, no cursor blink).
 *   • Canvas = radial gradient, Ari purple center → presidential dark blue edges.
 *   • Header = presidential dark blue, logo (left) + Veteran badge (right).
 *   • Ari is present in design + tone but never "types" on this page.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RemoddashLogo from '../components/RemoddashLogo';
import GatewayAriHero from '../components/GatewayAriHero';
import GatewayDemoLaunchers from '../components/GatewayDemoLaunchers';
import { toast } from 'sonner';
import { validateStoredSession } from '@/utils/sessionTimeout';
import { NC_LAUNCH_LOCKED, HOMEPAGE_SHOW_NC_CARD, HOMEPAGE_SHOW_TOOLS_CARD } from '../config/launchFlags';

// Veteran Owned badge — identical markup to the one used on the landing page.
const VeteranBadge = ({ size = 65, idPrefix = 'gw' }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}>
    <defs>
      <linearGradient id={`${idPrefix}-silverRing`} x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#FFFFFF" /><stop offset="30%" stopColor="#E5E5E5" />
        <stop offset="50%" stopColor="#A8A8A8" /><stop offset="70%" stopColor="#E5E5E5" /><stop offset="100%" stopColor="#FFFFFF" />
      </linearGradient>
      <linearGradient id={`${idPrefix}-blueBadge`} x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#1E40AF" /><stop offset="50%" stopColor="#1E3A8A" /><stop offset="100%" stopColor="#172554" />
      </linearGradient>
      <clipPath id={`${idPrefix}-innerCircle`}><circle cx="50" cy="50" r="38" /></clipPath>
    </defs>
    <circle cx="50" cy="50" r="48" fill={`url(#${idPrefix}-silverRing)`} stroke="#9CA3AF" strokeWidth="1" />
    <circle cx="50" cy="50" r="42" fill="none" stroke="#B0B0B0" strokeWidth="2" />
    <circle cx="50" cy="50" r="38" fill={`url(#${idPrefix}-blueBadge)`} />
    <g clipPath={`url(#${idPrefix}-innerCircle)`} opacity="0.4">
      <rect x="12" y="26" width="76" height="5" fill="#EF4444" />
      <rect x="12" y="36" width="76" height="5" fill="#EF4444" />
      <rect x="12" y="46" width="76" height="5" fill="#EF4444" />
      <rect x="12" y="56" width="76" height="5" fill="#EF4444" />
      <rect x="12" y="66" width="76" height="5" fill="#EF4444" />
      <rect x="12" y="26" width="32" height="25" fill="#3B82F6" />
    </g>
    <circle cx="50" cy="50" r="34" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
    <g fill="white" fontSize="6">
      <text x="30" y="33" textAnchor="middle">&#9733;</text><text x="38" y="32" textAnchor="middle">&#9733;</text>
      <text x="46" y="31" textAnchor="middle">&#9733;</text><text x="54" y="31" textAnchor="middle">&#9733;</text>
      <text x="62" y="32" textAnchor="middle">&#9733;</text><text x="70" y="33" textAnchor="middle">&#9733;</text>
    </g>
    <text x="50" y="50" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="Arial Black, Arial" letterSpacing="0.5">VETERAN</text>
    <text x="50" y="62" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontFamily="Arial Black, Arial" letterSpacing="0.5">OWNED</text>
    <g fill="white" fontSize="6">
      <text x="30" y="75" textAnchor="middle">&#9733;</text><text x="38" y="76" textAnchor="middle">&#9733;</text>
      <text x="46" y="77" textAnchor="middle">&#9733;</text><text x="54" y="77" textAnchor="middle">&#9733;</text>
      <text x="62" y="76" textAnchor="middle">&#9733;</text><text x="70" y="75" textAnchor="middle">&#9733;</text>
    </g>
  </svg>
);

const Gateway = () => {
  const navigate = useNavigate();

  // Phase G — Returning-user acceleration.
  // If the user has a valid local session we short-circuit the role-selection
  // screen and drop them straight into their destination. First-time and
  // signed-out visitors still see the gateway as designed.
  //   • Provider: auth_token + user_data  →  /dashboard
  //   • Customer: remodify_token + remodify_slug  →  /remodify/{slug}
  //   • Override: ?force_gateway=1 always shows the gateway (used by the
  //     "Switch role" affordance and by QA).
  // We gate the render behind `ready` so the gateway never flashes for a
  // returning user before the redirect happens.
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('force_gateway') === '1') {
        setReady(true);
        return;
      }

      const authToken = localStorage.getItem('auth_token');
      const userData = localStorage.getItem('user_data');
      if (authToken && userData) {
        // Founder session ruling 2026-09-27: validate server-side BEFORE
        // auto-forwarding. A stale/rotated/expired token renders the gateway
        // signed-out instead of redirect-flapping into /dashboard 401s.
        validateStoredSession().then((state) => {
          if (state === 'valid') navigate('/dashboard', { replace: true });
          else setReady(true);
        });
        return;
      }

      const remodifyToken = localStorage.getItem('remodify_token');
      const remodifySlug = localStorage.getItem('remodify_slug');
      if (remodifyToken && remodifySlug) {
        navigate(`/remodify/${remodifySlug}`, { replace: true });
        return;
      }
    } catch {
      // localStorage can throw in privacy modes — fall through and render.
    }
    setReady(true);
  }, [navigate]);

  // ─── CARD COPY (locked 2026-06-01).
  // First-time-user context text — purpose is to balance the card layout
  // and match the visual reference, NOT final marketing copy. Refining
  // wording later does NOT require touching the card structure.
  const CARDS_COPY = {
    customer: 'Find a trusted service provider for your needs.',
    provider: 'Offer your services and grow your business.',
    network_contractor: 'Build your network, connect customers and providers, and earn more.',
    tools: 'Manage forms, payments, inventory, and more.',
  };

  const handleRoleSelect = (role) => {
    if (role === 'customer') {
      navigate('/remodify');
    } else if (role === 'tools') {
      navigate('/tools');
    } else if (role === 'network_contractor') {
      if (NC_LAUNCH_LOCKED) {
        toast('Network Contractor is coming soon.', { description: 'This path opens once the provider network is live.' });
        return;
      }
      navigate('/nc-landing');
    } else {
      navigate('/landing');
    }
  };

  if (!ready) return null;

  return (
    <div
      className="min-h-screen bg-[radial-gradient(ellipse_at_center,_#3b0764_0%,_#4c1d95_25%,_#3b0764_55%,_#2a1065_72%,_#172554_92%,_#0a1230_100%)]"
      data-testid="gateway-page"
    >
      {/* Header — solid black, logo (left) + Veteran badge (right). KEPT per RemodDash brand identity exception. */}
      <header className="sticky top-0 z-50 backdrop-blur-md border-b bg-black border-black/80">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <RemoddashLogo type="full" height={54} className="flex-shrink-0" data-testid="gateway-logo" />
          <VeteranBadge size={65} idPrefix="gw-header" />
        </div>
      </header>

      {/* Decision surface — RECREATED from approved reference image (locked 2026-06-01).
          Layout, background, top-section structure, mini-header chips, card layout,
          card visuals — all pixel-matched to the reference. Only swaps:
          • RemodDash icons (Users / Briefcase / Network / Wrench)
          • Card labels (Customer / Provider / Network Contractor / Tools)
          • Click routing (/remodify, /landing, /nc-signup, /tools)
          • Top header (RemodDash brand identity exception)
          • Bottom compliance footer (RemodDash brand identity exception) */}
      <section className="relative overflow-hidden pt-16 sm:pt-20 pb-12 sm:pb-16 px-4 sm:px-6" data-testid="gateway-section">
        {/* ── Ari kiosk (replaces the static "Hi, I'm Ari." / "How can I help you today?" pair). ── */}
        <GatewayAriHero />

        {/* ── 4 interactive demo launchers (replace the static info chips).
              Each tinted its real card color; launches the Ari Demo Theater.
              Card layout / positioning / branding remain unchanged. ── */}
        <GatewayDemoLaunchers />

        {/* ── Helper text ── */}
        <p
          className="text-center text-sm text-slate-400 mb-10 sm:mb-12"
          data-testid="gateway-helper"
        >
          Watch a live demo above, or choose the path that fits you best.
        </p>

        {/* ── Gateway cards — raw card artwork only. Launch focus (2026-08-24):
              Customer + Provider are the public paths; the Network Contractor and
              Tools cards are gated behind HOMEPAGE_SHOW_* launch flags (frontend
              hide only — routes, pages and backends fully preserved for later
              restoration). No HTML overlays; click handlers, routing and testids
              preserved verbatim. */}
        <div
          className={`${HOMEPAGE_SHOW_NC_CARD || HOMEPAGE_SHOW_TOOLS_CARD
            ? 'max-w-[1480px] lg:grid-cols-4'
            : 'max-w-[880px]'} mx-auto px-2 sm:px-3 grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3`}
          data-testid="role-selection"
        >
          <button
            onClick={() => handleRoleSelect('customer')}
            className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400 rounded-xl transition-transform duration-300 hover:scale-[1.03]"
            aria-label="Customer"
            data-testid="role-select-customer"
          >
            <img
              src="/gateway-cards/customer.png"
              alt="Customer"
              className="block w-full h-auto select-none pointer-events-none"
              draggable="false"
            />
          </button>

          <button
            onClick={() => handleRoleSelect('provider')}
            className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded-xl transition-transform duration-300 hover:scale-[1.03]"
            aria-label="Provider"
            data-testid="role-select-provider"
          >
            <img
              src="/gateway-cards/provider.png"
              alt="Provider"
              className="block w-full h-auto select-none pointer-events-none"
              draggable="false"
            />
          </button>

          {HOMEPAGE_SHOW_NC_CARD && (
            <button
              onClick={() => handleRoleSelect('network_contractor')}
              className={`relative block focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 rounded-xl transition-transform duration-300 ${NC_LAUNCH_LOCKED ? 'cursor-not-allowed' : 'hover:scale-[1.03]'}`}
              aria-label="Network Contractor"
              aria-disabled={NC_LAUNCH_LOCKED}
              data-testid="role-select-network-contractor"
            >
              {NC_LAUNCH_LOCKED && (
                <span
                  className="absolute top-3 right-3 z-10 px-2.5 py-1 rounded-full bg-amber-400 text-black text-[11px] font-bold uppercase tracking-wide shadow-md"
                  data-testid="nc-coming-soon-badge"
                >
                  Coming Soon
                </span>
              )}
              <img
                src="/gateway-cards/nc.png"
                alt="Network Contractor"
                className="block w-full h-auto select-none pointer-events-none"
                draggable="false"
              />
            </button>
          )}

          {HOMEPAGE_SHOW_TOOLS_CARD && (
            <button
              onClick={() => handleRoleSelect('tools')}
              className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 rounded-xl transition-transform duration-300 hover:scale-[1.03]"
              aria-label="Tools"
              data-testid="role-select-tools"
            >
              <img
                src="/gateway-cards/tools.png"
                alt="Tools"
                className="block w-full h-auto select-none pointer-events-none"
                draggable="false"
              />
            </button>
          )}
        </div>

        <style>{`
          @keyframes ariBreath { 0%, 100% { box-shadow: 0 0 0 0 rgba(124,58,237,0); } 50% { box-shadow: 0 0 40px 8px rgba(124,58,237,0.25); } }
          .ari-breath { animation: ariBreath 3.2s ease-in-out infinite; }
          @keyframes ariFloat { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
          .ari-float { animation: ariFloat 4s ease-in-out infinite; }
          @media (prefers-reduced-motion: reduce) { .ari-float { animation: none; } }
        `}</style>
      </section>

      {/* Telnyx / business-compliance footer (appended Apr 2026). */}
      <div style={{
        width: "100%",
        marginTop: "40px",
        padding: "20px 16px",
        borderTop: "1px solid rgba(255,255,255,0.1)",
        textAlign: "center",
        fontSize: "12px",
        opacity: 0.8,
        color: "#fff"
      }} data-testid="gateway-compliance-footer">
        <div style={{ marginBottom: "8px" }}>
          <strong>RemodDash</strong> (powered by RemodWorks)
        </div>

        <div style={{ marginBottom: "8px" }}>
          Email: support@remoddash.com
        </div>

        <div style={{ marginBottom: "12px" }}>
          Location: Akron, Ohio
        </div>

        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: "12px",
          flexWrap: "wrap"
        }}>
          <a href="/" style={{ color: "#fff" }} data-testid="footer-link-home">Home</a>
          <a href="/services" style={{ color: "#fff" }} data-testid="footer-link-services">Industries</a>
          <a href="/about" style={{ color: "#fff" }} data-testid="footer-link-about">About</a>
          <a href="/privacy" style={{ color: "#fff" }} data-testid="footer-link-privacy">Privacy Policy</a>
          <a href="/terms" style={{ color: "#fff" }} data-testid="footer-link-terms">Terms of Service</a>
        </div>

        <div style={{ marginTop: "12px", opacity: 0.7 }} data-testid="gateway-copyright">
          {new Date().getFullYear()} RemodWorks. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default Gateway;
